import { Button, Dropdown, Radio } from 'components/core';
import {
  buildSubscriptionFrequenciesByQuantity,
  getAvailableSubscriptionQuantities,
  OneTimePurchasePrice,
  SubscriptionPurchasePrice,
} from '.';
import { SetStateAction, useState, useEffect } from 'react';
import { Product } from 'types';
import { useI18n } from 'contexts/i18n';
import * as Styled from './ProductSubscriptionOptions.styles';
import SubscriptionDiscountInfoModal from './SubscriptionDiscountInfoModal';
import { maxDiscount } from 'components/ProductTile/ProductTile.helper';
import { ClientSubscriptionProduct } from 'types/Subscription.types';
import { translateFrequencyLabel } from 'components/ProductDetails/ProductDetails.helper';

const ProductSubscriptionOptions = ({
  product,
  couponProduct,
  hasMultipleOffers,
  subscriptionProduct,
  addLineItemMutation,
  handleAddToCart,
  allowableQuantities,
  setOneTimePurchaseQuantity,
  oneTimePurchaseQuantity,
  setSubscriptionQuantity,
  subscriptionQuantity,
  setIsOneTimePurchaseSelected,
  isOneTimePurchaseSelected,
  selectedFrequencyId,
  setSelectedFrequencyId,
}: {
  product: Product;
  couponProduct: any;
  hasMultipleOffers: boolean;
  subscriptionProduct: ClientSubscriptionProduct;
  addLineItemMutation: any;
  handleAddToCart: (
    isSubscriptionChecked?: boolean,
    subscriptionFrequencyId?: string
  ) => void;
  allowableQuantities: { label: string; value: string }[];
  setOneTimePurchaseQuantity: (value: SetStateAction<string>) => void;
  oneTimePurchaseQuantity: string;
  setSubscriptionQuantity: (value: SetStateAction<string | undefined>) => void;
  subscriptionQuantity: string | undefined;
  setIsOneTimePurchaseSelected: (value: boolean) => void;
  isOneTimePurchaseSelected: boolean;
  selectedFrequencyId: string;
  setSelectedFrequencyId: (value: SetStateAction<string>) => void;
}) => {
  const [
    showSubscriptionDiscountInfoModal,
    setShowSubscriptionDiscountInfoModal,
  ] = useState(false);

  const [availableSubscriptionQuantities, setAvailableSubscriptionQuantities] =
    useState(getAvailableSubscriptionQuantities(subscriptionProduct));

  const [
    availableSubscriptionFrequencies,
    setAvailableSubscriptionFrequencies,
  ] = useState<{ label: string; value: string }[]>([]);

  const i18n = useI18n();

  const maxSubscriptionDiscount = maxDiscount(subscriptionProduct);

  const handleFrequencyChange = (frequencyId: string) => {
    setSelectedFrequencyId(frequencyId);
  };

  useEffect(() => {
    if (availableSubscriptionQuantities.length > 0) {
      const defaultQuantity = availableSubscriptionQuantities[0].value;

      const availableFrequencies = buildSubscriptionFrequenciesByQuantity(
        subscriptionProduct,
        parseInt(defaultQuantity)
      );

      setAvailableSubscriptionFrequencies(
        availableFrequencies.map(freq => translateFrequencyLabel(freq, i18n))
      );

      const defaultFrequency = availableFrequencies[0]?.value;

      setSubscriptionQuantity(defaultQuantity);
      setSelectedFrequencyId(defaultFrequency);
    }
  }, [
    availableSubscriptionQuantities,
    isOneTimePurchaseSelected,
    subscriptionProduct,
    setOneTimePurchaseQuantity,
    setSubscriptionQuantity,
    setSelectedFrequencyId,
  ]);

  return (
    <Styled.PurchaseOptions>
      <Styled.SubscriptionContainer
        isSelected={!isOneTimePurchaseSelected}
        onClick={() => setIsOneTimePurchaseSelected(false)}
      >
        <Radio
          label={i18n['Subscribe and Save'] || 'Subscribe and Save'}
          checked={!isOneTimePurchaseSelected}
          onChange={() => {
            setIsOneTimePurchaseSelected(false);
          }}
          circleColor={'rgba(0, 0, 0, 0.13)'}
        />
        <Styled.OptionDetails>
          <SubscriptionPurchasePrice
            subscriptionProduct={subscriptionProduct}
            selectedFrequencyId={selectedFrequencyId}
            subscriptionQuantity={subscriptionQuantity}
          />

          <Styled.Separator />

          <Styled.SubscriptionDiscountMessage>
            {i18n['Save upto']} {maxSubscriptionDiscount}%{' '}
            {i18n['for all deliveries.']}{' '}
            <span
              role="button"
              tabIndex={0}
              onClick={() => setShowSubscriptionDiscountInfoModal(true)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowSubscriptionDiscountInfoModal(true);
                }
              }}
            >
              {i18n['View All Subscription Plans'] ||
                'View All Subscription Plans'}
            </span>
          </Styled.SubscriptionDiscountMessage>

          {!isOneTimePurchaseSelected && (
            <>
              <Styled.QuantityWrapper data-testid="product-quantity">
                <div>{i18n['Quantity:']}</div>
                <Dropdown
                  options={availableSubscriptionQuantities}
                  onChange={val => {
                    setSubscriptionQuantity(val);
                    const availableFrequencies =
                      buildSubscriptionFrequenciesByQuantity(
                        subscriptionProduct,
                        parseInt(val)
                      );
                    setAvailableSubscriptionFrequencies(
                      availableFrequencies.map(freq =>
                        translateFrequencyLabel(freq, i18n)
                      )
                    );
                    const defaultFrequency = availableFrequencies[0]?.value;
                    setSelectedFrequencyId(defaultFrequency);
                  }}
                  selectedValue={subscriptionQuantity}
                  label="Qty"
                  size="sm"
                  width="30%"
                ></Dropdown>
              </Styled.QuantityWrapper>

              <Styled.QuantityWrapper data-testid="product-quantity">
                <div>{i18n['Deliver every:']}</div>
                <Dropdown
                  options={availableSubscriptionFrequencies}
                  onChange={handleFrequencyChange}
                  selectedValue={selectedFrequencyId}
                  label="Frequency"
                  size="sm"
                  width="100%"
                ></Dropdown>
              </Styled.QuantityWrapper>

              <Styled.ButtonWrapper>
                <Button
                  variant="primary"
                  disabled={
                    !product.inStock ||
                    addLineItemMutation.isLoading ||
                    subscriptionQuantity === '0' ||
                    selectedFrequencyId === ''
                  }
                  onClick={() =>
                    handleAddToCart(
                      !isOneTimePurchaseSelected,
                      selectedFrequencyId
                    )
                  }
                  loading={addLineItemMutation.isLoading}
                  size="md"
                  width="100%"
                >
                  {product.inStock ? i18n['Add to cart'] : i18n['Out of Stock']}
                </Button>
              </Styled.ButtonWrapper>
            </>
          )}
        </Styled.OptionDetails>
      </Styled.SubscriptionContainer>

      <Styled.SubscriptionContainer
        isSelected={isOneTimePurchaseSelected}
        onClick={() => setIsOneTimePurchaseSelected(true)}
      >
        <Radio
          label={`${i18n['One-time Purchase']}`}
          checked={isOneTimePurchaseSelected}
          onChange={() => {
            setIsOneTimePurchaseSelected(true);
            setOneTimePurchaseQuantity(allowableQuantities[0].value);
          }}
          circleColor={'rgba(0, 0, 0, 0.13)'}
        />

        <Styled.OptionDetails>
          <OneTimePurchasePrice
            product={product}
            couponProduct={couponProduct}
            hasMultipleOffers={hasMultipleOffers}
          />

          {isOneTimePurchaseSelected && (
            <>
              <Styled.Separator />
              <Styled.QuantityWrapper data-testid="product-quantity">
                <div>{i18n['Quantity:']}</div>
                <Dropdown
                  options={allowableQuantities}
                  onChange={val => {
                    setOneTimePurchaseQuantity(val);
                  }}
                  selectedValue={oneTimePurchaseQuantity}
                  label="Qty"
                  size="sm"
                  width="30%"
                ></Dropdown>
              </Styled.QuantityWrapper>

              <Styled.ButtonWrapper>
                <Button
                  variant="primary"
                  disabled={
                    !product.inStock ||
                    addLineItemMutation.isLoading ||
                    oneTimePurchaseQuantity === '0'
                  }
                  onClick={() => handleAddToCart()}
                  loading={addLineItemMutation.isLoading}
                  size="md"
                  width="90%"
                >
                  {product.inStock ? i18n['Add to cart'] : i18n['Out of Stock']}
                </Button>
              </Styled.ButtonWrapper>
            </>
          )}
        </Styled.OptionDetails>
      </Styled.SubscriptionContainer>

      {showSubscriptionDiscountInfoModal && (
        <SubscriptionDiscountInfoModal
          product={product}
          subscriptionProduct={subscriptionProduct}
          onClose={() => setShowSubscriptionDiscountInfoModal(false)}
          setSelectedFrequencyId={setSelectedFrequencyId}
          selectedFrequencyId={selectedFrequencyId}
          setAvailableSubscriptionFrequencies={
            setAvailableSubscriptionFrequencies
          }
          setSubscriptionQuantity={setSubscriptionQuantity}
          subscriptionQuantity={subscriptionQuantity}
          handleAddToCart={handleAddToCart}
        />
      )}
    </Styled.PurchaseOptions>
  );
};

export default ProductSubscriptionOptions;
